import { toSortedCompat } from '@sweetistics/contracts/utils/sort';
import { getNumberFormatter } from '@/lib/formatters/numbers';
import { formatNumber } from '@/lib/utils';
import type { CompareEngagementTimeMetric } from '../types';

const ORDERING = ['6h', '12h', '24h', '3d', '7d'];

const comparisonAverageFormatter = getNumberFormatter('en-US', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 1,
});

export const formatAverageMetric = (value: number): string => {
  if (!Number.isFinite(value)) {
    return '0';
  }
  const rounded = Math.round(value * 10) / 10;
  if (Math.abs(rounded) >= 1000) {
    return formatNumber(rounded);
  }
  if (Number.isInteger(rounded)) {
    return formatNumber(rounded);
  }
  return comparisonAverageFormatter.format(rounded);
};

export const formatComparisonPeriod = (period: string): string => {
  switch (period) {
    case '6h': {
      return 'Last 6 hours';
    }
    case '12h': {
      return 'Last 12 hours';
    }
    case '24h': {
      return 'Last 24 hours';
    }
    case '3d': {
      return 'Last 3 days';
    }
    case '7d': {
      return 'Last 7 days';
    }
    default: {
      return period;
    }
  }
};

export const sortPeriods = <T extends CompareEngagementTimeMetric>(metrics: T[]): T[] =>
  toSortedCompat(metrics, (a, b) => {
    const aIndex = ORDERING.indexOf(a.period);
    const bIndex = ORDERING.indexOf(b.period);
    if (aIndex === -1 && bIndex === -1) {
      return a.period.localeCompare(b.period);
    }
    if (aIndex === -1) {
      return 1;
    }
    if (bIndex === -1) {
      return -1;
    }
    return aIndex - bIndex;
  });
